import React from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

interface MessageNotificationProps {
	id: string | number;
	channelName: string;
	senderName: string;
	senderAvatar?: string | null;
	message: string;
	timestamp?: string;
	onAction?: () => void;
}

export const MessageNotification: React.FC<MessageNotificationProps> = ({
	id,
	channelName,
	senderName,
	senderAvatar,
	message,
	timestamp = "jetzt",
	onAction,
}) => {
	// Generate initials from sender name for avatar fallback
	const getInitials = (name: string) => {
		const parts = name.split(' ');
		if (parts.length >= 2) {
			return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		}
		return name.slice(0, 2).toUpperCase();
	};

	return (
		<div 
			className="relative group flex items-start gap-3 bg-slate-700 rounded-lg shadow-xl p-4 min-w-[360px] max-w-[420px] cursor-pointer hover:bg-slate-600 transition-colors"
			onClick={() => {
				if (onAction) {
					onAction();
					toast.dismiss(id);
				}
			}}
		>
			{/* Avatar */}
			<div className="flex-shrink-0">
				{senderAvatar ? (
					<img
						src={senderAvatar}
						alt={senderName}
						className="w-10 h-10 rounded-md object-cover"
					/>
				) : (
					<div className="w-10 h-10 rounded-md bg-gray-600 flex items-center justify-center">
						<span className="text-sm font-medium text-white">
							{getInitials(senderName)}
						</span>
					</div>
				)}
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex items-baseline gap-2 mb-0.5">
					<span className="font-semibold text-white text-sm">
						{senderName}
					</span>
					<span className="text-xs text-gray-400">
						{timestamp}
					</span>
				</div>
				<p className="text-xs text-gray-400 mb-1">
					{channelName}
				</p>
				<p className="text-sm text-gray-200 line-clamp-2">
					{message}
				</p>
			</div>

			{/* Close button */}
			<button
				onClick={(e) => {
					e.stopPropagation();
					toast.dismiss(id);
				}}
				className="absolute top-2 right-2 text-gray-500 hover:text-gray-300 transition-colors opacity-0 group-hover:opacity-100"
				style={{ opacity: 1 }}
			>
				<X className="w-4 h-4" />
			</button>
		</div>
	);
};

export const showMessageNotification = ({
	channelName,
	senderName,
	senderAvatar,
	message,
	timestamp,
	duration = 8000,
	onAction,
}: {
	channelName: string;
	senderName: string;
	senderAvatar?: string | null;
	message: string;
	timestamp?: string;
	duration?: number;
	onAction?: () => void;
}) => {
	return toast.custom(
		(t) => (
			<MessageNotification
				id={t}
				channelName={channelName}
				senderName={senderName}
				senderAvatar={senderAvatar}
				message={message}
				timestamp={timestamp}
				onAction={onAction}
			/>
		),
		{
			duration,
			position: "top-right",
			className: "slack-notification",
		}
	);
};